import { Badge, Card } from '@safetyhub/ui';
import {
  agencyRelabeledTitle,
  type LegalObligationAgencyAdditionView,
  type LegalObligationRow,
} from '@safetyhub/shared';

/**
 * 이 조각들이 읽는 칸 — 의무 목록 행(`LegalObligationRow`)과 연간 격자 행(`LegalObligationAnnualRow`)이 같은
 * 형상으로 싣는다(서버가 같은 `buildAgencyOverlayApplication`으로 만든다). 두 화면이 사본 없이 이 조각을 쓴다.
 */
type AgencyOverlayRow = Pick<LegalObligationRow, 'obligationId' | 'title' | 'agencyOverlay'>;

/**
 * 대행 기관의 **서식 변형**(2층)을 의무 목록 위에 그리는 조각들 — 서버 `buildAgencyOverlayApplication`이 만든 형상
 * (`LegalObligationRow.agencyOverlay` · `LegalObligationsResponse.agencyAdditions`)을 그대로 읽는다.
 *
 * - 제목은 **기관이 쓰는 이름이 먼저**이고 공통 서식 이름을 아래에 함께 보인다(대행 기관 직원은 자기 서식 어휘로
 *   찾고, 수검업체는 공통 어휘로 찾는다 — 한쪽만 보이면 다른 쪽이 못 찾는다).
 * - `OMIT`도 행을 **지우지 않는다** — 「기관 서식에서 쓰지 않음」으로 보이고 이행 기록은 만들어지지 않는다
 *   (그 결정은 서버 실체화 층의 몫이다 · 1층 「해당없음」 제외가 이긴다).
 * - 의무 이름을 받는 창(증빙 등록·증빙 목록·사건 등록·마지막 실시일·제외 모달과 사건 취소·제외 해제 확인창)도
 *   **목록 행과 같은 이름**이다(사용자 결정 2026-10-30) — 호스트 `SiteObligationsPage`가 이 함수로 넘긴다.
 */
export function agencyDisplayTitle(row: AgencyOverlayRow): string {
  return agencyRelabeledTitle(row.agencyOverlay, row.title) ?? row.title;
}

export function AgencyOverlayNote({ row }: { row: AgencyOverlayRow }) {
  const overlay = row.agencyOverlay;
  if (!overlay) return null;
  const relabeled = agencyRelabeledTitle(overlay, row.title) !== null;
  if (!relabeled && !overlay.omittedByAgency && overlay.parts.length === 0) return null;
  return (
    <div className="mt-1 space-y-1 text-xs text-ink-500" data-testid={`agency-overlay-${row.obligationId}`}>
      {relabeled && <p>공통 서식 이름: {row.title}</p>}
      {overlay.omittedByAgency && (
        <Badge tone="slate" size="sm">
          기관 서식에서 쓰지 않음
        </Badge>
      )}
      {overlay.parts.length > 0 && (
        <p>
          기관 서식에서 나눈 항목: {overlay.parts.map((p) => p.title ?? p.overlayKey).join(' · ')}
        </p>
      )}
    </div>
  );
}

export function AgencyAdditionsCard({ additions }: { additions: LegalObligationAgencyAdditionView[] }) {
  if (additions.length === 0) return null;
  return (
    <Card padding="none" radius="lg" elevation="md" className="mt-4 p-4">
      <p className="text-sm font-bold text-ink-900">기관 서식에만 있는 항목</p>
      <p className="mt-0.5 text-xs text-ink-500">
        대행 기관이 더한 항목입니다. 법정 의무 목록에는 없어 이행 기록이 만들어지지 않습니다.
      </p>
      <ul className="mt-2 space-y-1 text-sm text-ink-700" aria-label="기관 서식에만 있는 항목">
        {additions.map((a) => (
          <li key={a.overlayKey}>
            {a.title ?? a.overlayKey}
            {a.description && <span className="ml-1 text-xs text-ink-500">— {a.description}</span>}
          </li>
        ))}
      </ul>
    </Card>
  );
}
